import { useConvertToDevboxModal } from "components/GlobalModal/useConvertToDevboxModal";
import { useVSCodePromptModal } from "components/GlobalModal/useVSCodePromptModal";
import { useRegisterCommands } from "components/Layout/Panels/VSCode/useRegisterCommands";
import { useEnvironmentInterface } from "environment-interface";
import { useEditor } from "features/editor/Feature";
import { useAddStatusNotification } from "features/notifications/hooks";
import { useSession } from "hooks/api/useSession";
import {
  hasBranchWriteAccess,
  hasSandboxPermission,
} from "isomorphic/permission";
import { Button, Dropdown, Tooltip, DropdownButton } from "prism-react";
import React from "react";

import { useActiveFile } from "hooks/useActiveFile";
import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useAnalytics } from "hooks/useAnalytics";
import { useAutoLaunchVSCode } from "hooks/useAutoLaunchVSCode";
import { useMissingVsCodeInsidersNotification } from "hooks/useMissingVsCodeInsidersNotification";
import type { OpenInVsCodeOptions } from "hooks/useOpenInVsCode";
import { editorInfo, useOpenInVsCodeInline } from "hooks/useOpenInVsCode";
import { useGlobalPersistedState } from "hooks/usePersistedState";
import type { OpenEditorType } from "utils/buildExtensionUri";
import { UnknownProtocolError } from "utils/url";
import { LOCAL_EDITOR_PREFERENCE } from "utils/vscode";

const TrackEditorSelector = () => {
  const track = useAnalytics();

  React.useEffect(() => {
    track("editor_open_editor_options");
  }, [track]);

  return null;
};

export const OpenLocalButton: React.FC = () => {
  const track = useAnalytics();
  const [session] = useSession();
  const { sentry } = useEnvironmentInterface();
  const [activeFile] = useActiveFile();
  const convertSandboxModal = useConvertToDevboxModal();
  const activeWorkspace = useActiveWorkspace();

  const editor = useEditor();
  const dispatchNotification = useAddStatusNotification();
  const dispatchMissingVsCodeInsidersNotification =
    useMissingVsCodeInsidersNotification();

  const [autoLaunchVSCode] = useAutoLaunchVSCode();
  const [localEditorToUse, setLocalEditorPreference] =
    useGlobalPersistedState<OpenEditorType>(LOCAL_EDITOR_PREFERENCE, "vscode");

  const [openInVsCodeState, openInVsCode] = useOpenInVsCodeInline({
    editor,
    activeFilepath: activeFile?.path,
  });

  const vscodePromptModal = useVSCodePromptModal();

  const canOpenLocally =
    session.state === "AUTHENTICATED" && editor.allowedCapabilities.vscode;

  const isBrowserSandbox =
    editor.type === "sandbox" && editor.isCloud === false;
  const canConvertSandbox =
    isBrowserSandbox &&
    editor.authorization === "owner" &&
    activeWorkspace &&
    editor.isFrozen === false;

  const launchLocalEditor = (options?: OpenInVsCodeOptions) => {
    if (autoLaunchVSCode) {
      return openInVsCode(options)
        .then(() => {
          setLocalEditorPreference(options?.type || "vscode");
        })
        .catch((error) => {
          // Handle unknown "vscode-insiders://" protocol error.
          // This error occurs when the user doesn't have VS Code Insiders
          // installed on their machine.
          if (error instanceof UnknownProtocolError) {
            dispatchMissingVsCodeInsidersNotification();
            return;
          }

          sentry.reportError(error);

          dispatchNotification({
            status: "error",
            message: `Failed to launch ${primaryEditor.type}`,
            actions: [
              {
                text: "Try again",
                onAction: launchLocalEditor,
              },
            ],
          });
        });
    }

    vscodePromptModal.open({
      editor,
      activeFilepath: activeFile?.path ?? null,
      type: options?.type || "vscode",
    });
    setLocalEditorPreference(options?.type || "vscode");
  };

  useRegisterCommands(
    canOpenLocally
      ? [
          {
            id: "codesandbox.integrations.openInVSCode",
            name: "Open in VS Code",
            perform() {
              track("vs_code", { event_source: "command_palette" });
              launchLocalEditor();
            },
            section: "Integrations",
          },
          {
            id: "codesandbox.integrations.openInVSCodeInsiders",
            name: "Open in VS Code Insiders",
            perform() {
              track("vs_code", {
                event_source: "command_palette",
                insiders: true,
                type: "vscode-insiders",
              });
              launchLocalEditor({ type: "vscode-insiders" });
            },
            section: "Integrations",
          },
          {
            id: "codesandbox.integrations.openInCursor",
            name: "Open in Cursor",
            perform() {
              track("vs_code", {
                event_source: "command_palette",
                type: "cursor",
              });
              launchLocalEditor({ type: "cursor" });
            },
            section: "Integrations",
          },
        ]
      : [],
    [canOpenLocally, openInVsCode],
  );

  const handleOpenEditor = (type: OpenEditorType) => () => {
    track("vs_code", { event_source: "editor", type });
    launchLocalEditor({ type });
  };

  const withLoadingLabel = (label: string): string => {
    return openInVsCodeState.state === "loading" ? "Opening..." : label;
  };

  const primaryEditor = {
    type: localEditorToUse,
    editor: editorInfo[localEditorToUse],
  };
  const secondaryEditors = (Object.keys(editorInfo) as OpenEditorType[]).map(
    (k) => ({ type: k, editor: editorInfo[k] }),
  );

  // Compose different primary/secondary button relationships
  // based on the previous choice from the user.
  const openVscodeButtons = {
    primary: {
      label: withLoadingLabel(`Open in ${primaryEditor.editor.name}`),
      onClick: handleOpenEditor(primaryEditor.type),
    },
    secondary: secondaryEditors.map((editor) => ({
      label: withLoadingLabel(`Open in ${editor.editor.name}`),
      onClick: handleOpenEditor(editor.type),
    })),
  };

  const typeName = editor.type === "branch" ? "branch" : "devbox";
  const actionToPerform =
    editor.type === "branch" ? "Branch out" : "Fork this devbox";

  const hasWritePermission =
    editor.type === "branch"
      ? hasBranchWriteAccess(editor.authorization)
      : hasSandboxPermission(editor.authorization, "write_project");

  // Content displayed when the open in vscode button is disabled
  let disabledTooltipContent: string;

  if (isBrowserSandbox) {
    disabledTooltipContent = "Sandboxes cannot be opened in VS Code";
  } else if (session.state !== "AUTHENTICATED") {
    disabledTooltipContent = `Sign in to open this ${typeName} in VS Code`;
  } else if (!hasWritePermission) {
    disabledTooltipContent = `You need write permissions to open this in VS Code.`;
  } else {
    disabledTooltipContent = `Unable to open VS Code on a protected ${typeName}. ${actionToPerform} to open VS Code`;
  }

  const component = canConvertSandbox ? (
    <Tooltip
      content={`To use ${primaryEditor.editor.name}, you need to convert this Sandbox to a Devbox`}
    >
      <Button
        onClick={() => {
          track("convert_to_devbox_open", { location: "vscode" });
          convertSandboxModal.open();
        }}
        variant="secondary"
      >
        {primaryEditor.editor.name}
      </Button>
    </Tooltip>
  ) : canOpenLocally ? (
    <DropdownButton
      buttonContent={openVscodeButtons.primary.label}
      disabled={openInVsCodeState.state === "loading"}
      onClick={openVscodeButtons.primary.onClick}
      variant="secondary"
    >
      <TrackEditorSelector />
      {openVscodeButtons.secondary.map((item) => (
        <Dropdown.Item key={item.label} onClick={item.onClick}>
          {item.label}
        </Dropdown.Item>
      ))}
    </DropdownButton>
  ) : (
    <Tooltip content={disabledTooltipContent}>
      <Button variant="secondary" disabled>
        {primaryEditor.editor.name}
      </Button>
    </Tooltip>
  );

  return component;
};
